// This type should be used in functions that require a client ID but don't need
// to inspect it, for example they just need to check a feature.
export type ClientId = string & { readonly __tag: unique symbol };
export const makeClientId = (clientId: string): ClientId =>
  clientId as ClientId;

// This type should be used in cases where you want to verify something is a
// known client, for example hard coded data objects.
export type KnownClientId = (typeof KNOWN_CLIENT_IDS)[number];
export type KnownDevClientId = (typeof KNOWN_DEV_CLIENT_IDS)[number];

const KNOWN_CLIENT_IDS = ["pantheon", "bluesky", "uip", "canopius"] as const;
const KNOWN_DEV_CLIENT_IDS = ["bluesky_dev", "uip_dev"] as const;
const DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS: { [dev: string]: KnownClientId } = {
  bluesky_dev: "bluesky",
  uip_dev: "uip",
} as const;

type FeatureFlags = {
  [K in KnownClientId]?: boolean;
};

function inferKeys<const T extends Record<string, FeatureFlags>>(features: T) {
  for (const clients of Object.values(features)) {
    for (const client_id of Object.keys(clients)) {
      if (!(KNOWN_CLIENT_IDS as readonly string[]).includes(client_id)) {
        throw Error(`Bad feature client_id ${client_id}`);
      }
    }
  }
  return features;
}
const FEATURES = inferKeys({
  "avatar-menu": {
    pantheon: true,
    bluesky: true,
    uip: true,
  },
  "bluesky-populate-google-sheets": {
    pantheon: true,
    bluesky: true,
  },
  "feedback-button": {
    uip: true,
    bluesky: true,
  },
  "handler-column": {
    bluesky: true,
    pantheon: true,
    uip: true,
  },
  "location-count-not-include-unique-buildings": {
    uip: true,
  },
  // If you enable this for other clients, make sure the Dashboard
  // logic still works. It assumes the use of a precomputed_metadata field
  "appetite-filtering": {
    uip: true,
  },
  "layers-card": {
    pantheon: true,
  },
  "loss-run-card": {
    pantheon: true,
    uip: true,
  },
  "loss-control-reports-card": {
    pantheon: true,
    canopius: true,
  },
  "uaw-card": {
    pantheon: true,
    uip: true,
  },
  "company-news-card": {
    pantheon: true,
    canopius: true,
  },
  "policy-review-questions": {
    pantheon: true,
  },
  "bluesky-underwriter-handler-separation": {
    bluesky: true,
  },
  "sl2-verification-template-editor": {
    pantheon: true,
  },
});

export type Feature = keyof typeof FEATURES;

export const featureEnabled = (
  clientId: ClientId,
  feature: Feature
): boolean => {
  if (clientId in DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS) {
    clientId = makeClientId(DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS[clientId]);
  }
  const casted = FEATURES as Record<string, Record<string, boolean>>;
  return casted[feature]?.[clientId] ?? false;
};

export function perClient<T>(map: { [clientId in KnownClientId]?: T }): {
  [clientId: ClientId]: T | undefined;
} {
  return {
    ...map,
    ...Object.fromEntries(
      Object.entries(DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS).map(([dev, prod]) => [
        dev,
        map[prod],
      ])
    ),
  };
}

export const resolveProductionClientId = (clientId: ClientId): ClientId => {
  const prod = DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS[clientId];
  return prod ? makeClientId(prod) : clientId;
};
